import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { taskService } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Estilos para la página de detalle
const styles = {
  taskDetailContainer: {
    marginBottom: '50px'
  },
  taskStatus: {
    fontSize: '0.9rem'
  },
  taskDetails: {
    marginTop: '20px'
  },
  detailItem: {
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center'
  },
  tokenItem: {
    fontWeight: '600',
    fontSize: '1.1rem'
  },
  mainImage: {
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  thumbnails: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px'
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '4px',
    overflow: 'hidden',
    cursor: 'pointer',
    opacity: '0.7',
    transition: 'opacity 0.3s, transform 0.3s'
  },
  activeThumbnail: {
    opacity: '1',
    border: '2px solid #0d6efd'
  }
};

const TaskDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  
  useEffect(() => {
    const fetchTaskDetail = async () => {
      try {
        setLoading(true);
        const response = await taskService.getTaskById(id);
        setTask(response.data);
      } catch (err) {
        console.error('Error al cargar detalles de la tarea:', err);
        setError('No se pudo cargar la tarea. Por favor, intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTaskDetail();
  }, [id]);
  
  const handleAcceptTask = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/tasks/${id}` } });
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await taskService.acceptTask(id);
      setTask(response.data.task);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al aceptar la tarea');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCompleteTask = async () => {
    try {
      setActionLoading(true);
      const response = await taskService.completeTask(id);
      setTask(response.data.task);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al completar la tarea');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleApproveTask = async () => {
    try {
      setActionLoading(true);
      const response = await taskService.approveTask(id);
      setTask(response.data.task);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al aprobar la tarea');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleStartChat = () => {
    if (task.createdBy._id === currentUser.id && task.acceptedBy) {
      navigate(`/chat/${task.acceptedBy._id}`);
    } else if (task.acceptedBy && task.acceptedBy._id === currentUser.id) {
      navigate(`/chat/${task.createdBy._id}`);
    }
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando detalles de la tarea...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger my-5" role="alert">
        {error}
      </div>
    );
  }
  
  if (!task) {
    return (
      <div className="alert alert-warning my-5" role="alert">
        No se encontró la tarea.
      </div>
    );
  }
  
  // Determinar si el usuario actual es el creador o colaborador
  const isCreator = currentUser && task.createdBy._id === currentUser.id;
  const isCollaborator = currentUser && task.acceptedBy && task.acceptedBy._id === currentUser.id;
  
  // Formatear fecha
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };
  
  return (
    <div style={styles.taskDetailContainer} className="my-4">
      <div className="row">
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h1 className="card-title">{task.title}</h1>
                <span className="badge bg-primary">{task.category}</span>
              </div>
              
              <div style={styles.taskStatus} className="mb-3">
                <span className={`badge ${
                  task.status === 'pendiente' ? 'bg-warning' : 
                  task.status === 'aceptada' ? 'bg-info' : 
                  task.status === 'culminada' ? 'bg-secondary' : 
                  'bg-success'
                }`}>
                  {task.status === 'pendiente' ? 'Pendiente' : 
                   task.status === 'aceptada' ? 'En progreso' : 
                   task.status === 'culminada' ? 'Completada' : 
                   'Aprobada'}
                </span>
              </div>
              
              <p className="card-text">{task.description}</p>
              
              <div style={styles.taskDetails}>
                <div style={styles.detailItem}>
                  <i className="fas fa-map-marker-alt me-2"></i>
                  <span>{task.location}</span>
                </div>
                
                <div style={styles.detailItem}>
                  <i className="far fa-calendar-alt me-2"></i>
                  <span>Creada: {formatDate(task.createdAt)}</span>
                </div>
                
                {task.completedAt && (
                  <div style={styles.detailItem}>
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Completada: {formatDate(task.completedAt)}</span>
                  </div>
                )}
                
                <div style={{...styles.detailItem, ...styles.tokenItem}}>
                  <i className="fas fa-coins me-2"></i>
                  <span>{task.tokens} BLUE Tokens</span>
                </div>
              </div>
            </div>
          </div>
          
          {task.images && task.images.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Imágenes</h5>
                <div>
                  <div style={styles.mainImage}>
                    <img 
                      src={task.images[currentImage]} 
                      alt={`Imagen ${currentImage + 1}`} 
                      className="img-fluid rounded"
                    />
                  </div>
                  
                  {task.images.length > 1 && (
                    <div style={styles.thumbnails}>
                      {task.images.map((img, index) => (
                        <div 
                          key={index}
                          style={{
                            ...styles.thumbnail,
                            ...(currentImage === index ? styles.activeThumbnail : {})
                          }}
                          onClick={() => setCurrentImage(index)}
                        >
                          <img 
                            src={img} 
                            alt={`Miniatura ${index + 1}`} 
                            style={{width: '100%', height: '100%', objectFit: 'cover'}}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Creado por</h5>
              <div className="creator-info">
                <p>{task.createdBy.name}</p>
              </div>
              
              {task.acceptedBy && (
                <>
                  <h5 className="card-title mt-4">Colaborador</h5>
                  <div className="collaborator-info">
                    <p>{task.acceptedBy.name}</p>
                  </div>
                </>
              )}
              
              {currentUser && (
                <div className="task-actions mt-4">
                  {task.status === 'pendiente' && !isCreator && (
                    <button 
                      className="btn btn-primary w-100" 
                      onClick={handleAcceptTask}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Procesando...' : 'Aceptar Tarea'}
                    </button>
                  )}
                  
                  {task.status === 'aceptada' && isCollaborator && (
                    <button 
                      className="btn btn-success w-100" 
                      onClick={handleCompleteTask}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Procesando...' : 'Marcar como Completada'}
                    </button>
                  )}
                  
                  {task.status === 'culminada' && isCreator && (
                    <button 
                      className="btn btn-success w-100" 
                      onClick={handleApproveTask}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Procesando...' : 'Aprobar y Pagar'}
                    </button>
                  )}
                  
                  {(isCreator || isCollaborator) && task.acceptedBy && (
                    <Link 
                      to={`/messages/${isCreator ? task.acceptedBy._id : task.createdBy._id}`}
                      className="btn btn-outline-primary w-100 mt-2"
                    >
                      <i className="fas fa-comments me-2"></i>
                      Enviar mensaje
                    </Link>
                  )}
                  
                  {(task.status === 'aceptada' || task.status === 'culminada') && 
                   (currentUser._id === task.createdBy._id || currentUser._id === task.acceptedBy?._id) && (
                    <button 
                      onClick={() => navigate(`/chat/${
                        currentUser._id === task.createdBy._id 
                          ? task.acceptedBy._id 
                          : task.createdBy._id
                      }`)} 
                      className="btn btn-outline-primary mt-3"
                    >
                      <i className="fas fa-comments me-2"></i>
                      Chat con {currentUser._id === task.createdBy._id 
                        ? task.acceptedBy.name 
                        : task.createdBy.name}
                    </button>
                  )}
                </div>
              )}
              
              {!currentUser && (
                <div className="mt-4">
                  <Link to="/login" className="btn btn-primary w-100">
                    Inicia sesión para interactuar
                  </Link>
                </div>
              )}
            </div>
          </div>
          
          {task.status === 'aprobada' && task.transactionHash && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Información de pago</h5>
                <div className="payment-info">
                  <div style={styles.detailItem}>
                    <i className="fas fa-exchange-alt me-2"></i>
                    <span>Transacción: {task.transactionHash.substring(0, 10)}...</span>
                  </div>
                  <div style={styles.detailItem}>
                    <i className="fas fa-coins me-2"></i>
                    <span>Comisión: {task.commission} BLUE</span>
                  </div>
                  <div style={styles.detailItem}>
                    <i className="fas fa-coins me-2"></i>
                    <span>Total: {task.totalMinted} RED</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
